import * as React from "react";
import Box from "@mui/joy/Box";
import Loading from "./Utils/Loading";
import Grid from "@mui/joy/Grid";
import CustomCard from "./Cards/CustomChallengeCard";
import NoDataComponent from "./Utils/NoData";
import { db } from "../FirebaseConfig";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import { UserAuth } from "../context/AuthContext";

export default function Challenges() {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<any>([]);
  const [difficultyFilter, setDifficultyFilter] = React.useState("all");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const { userData } = UserAuth();
  const [userSubmissions, setUserSubmissions] = React.useState<any[]>([]);

  const typeConfig: { [key: string]: { color: string } } = {
    Easy: { color: "success" },
    Moderate: { color: "warning" },
    Hard: { color: "danger" },
  };

  React.useEffect(() => {
    const getData = async () => {
      try {
        // Fetch challenges data
        const ctfSnapshot = await db
          .collection("ctf")
          .where("live", "==", true)
          .orderBy("createdAt", "desc")
          .get();

        const ctfData = ctfSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setData(ctfData);

        // Fetch user submissions
        const submissionsSnapshot = await db
          .collection("ChallengeSubmissions")
          .where("userid", "==", userData?.id)
          .get();
        const submissionsData = submissionsSnapshot.docs.map((d) => d.data());

        setUserSubmissions(submissionsData); // Store user's solved challenges
      } catch (error) {
        console.error("Error fetching data: ", error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [userData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleDifficultyFilterChange = (event: any, value: string | null) => {
    setDifficultyFilter(value ?? "all");
  };

  const handleCategoryFilterChange = (event: any, value: string | null) => {
    setCategoryFilter(value ?? "all");
  };

  const isChallengeSolved = (challengeName: string) => {
    return userSubmissions.some(
      (submission) => submission.name === challengeName
    );
  };

  const addSolvedChallenge = (solvedChallenge: any) => {
    setUserSubmissions((prevSubmissions) => [
      ...prevSubmissions,
      solvedChallenge,
    ]);
  };

  const filteredRows = data
    .filter((item: any) => {
      // Check if row matches the search query
      const matchesSearchQuery =
        searchQuery.length === 0 ||
        (item.name && item.name.toLowerCase().includes(searchQuery)) ||
        (item.category && item.category.toLowerCase().includes(searchQuery)) ||
        (item.description &&
          item.description.toLowerCase().includes(searchQuery)) ||
        (item.difficulty &&
          item.difficulty.toLowerCase().includes(searchQuery)) ||
        (item.points &&
          item.points.toString().toLowerCase().includes(searchQuery)) ||
        (item.flag && item.flag.toLowerCase().includes(searchQuery)) ||
        (item.hints && item.hints.toLowerCase().includes(searchQuery));

      // Check if row matches the role filter
      const matchesDifficultyFilter =
        difficultyFilter === "all" || item.difficulty === difficultyFilter;

      const matchesCategoryFilter =
        categoryFilter === "all" || item.category === categoryFilter;

      // Return true if row matches all criteria
      return (
        matchesSearchQuery && matchesCategoryFilter && matchesDifficultyFilter
      );
    })
    .sort((a: any, b: any) => {
      const aSolved = isChallengeSolved(a.name) ? 1 : 0;
      const bSolved = isChallengeSolved(b.name) ? 1 : 0;
      return aSolved - bSolved;
    });

  const getUniqueFilterOptions = (key: any) => {
    const allItems = data.map((item: any) => item[key]);
    const flatItems = [].concat(...allItems);
    const uniqueItems = Array.from(new Set(flatItems));
    return uniqueItems;
  };

  const renderFilters = () => {
    const difficultyOptions = getUniqueFilterOptions("difficulty");
    const categoryOptions = getUniqueFilterOptions("category");

    return (
      <React.Fragment>
        <FormControl size="sm">
          <FormLabel>Category</FormLabel>
          <Select
            size="sm"
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
            placeholder="All"
          >
            <Option value="all">All</Option>
            {categoryOptions.map((category) => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl size="sm">
          <FormLabel>Difficulty Level</FormLabel>
          <Select
            size="sm"
            value={difficultyFilter}
            onChange={handleDifficultyFilterChange}
            placeholder="All"
          >
            <Option value="all">All</Option>
            {difficultyOptions.map((difficulty) => (
              <Option key={difficulty} value={difficulty}>
                {difficulty}
              </Option>
            ))}
          </Select>
        </FormControl>
      </React.Fragment>
    );
  };

  return loading ? (
    <Loading />
  ) : (
    <React.Fragment>
      <Sheet
        className="SearchAndFilters-mobile"
        sx={{
          display: { xs: "flex", sm: "none" },
          my: 1,
          gap: 1,
        }}
      >
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          sx={{ flexGrow: 1 }}
          onChange={(event) => handleSearchChange(event)}
        />
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          onClick={() => setOpen(true)}
        >
          <FilterAltIcon />
        </IconButton>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
            <ModalClose />
            <Typography id="filter-modal" level="h2">
              Filters
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Sheet sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {renderFilters()}
              <Button color="primary" onClick={() => setOpen(false)}>
                Submit
              </Button>
            </Sheet>
          </ModalDialog>
        </Modal>
      </Sheet>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        <FormControl sx={{ flex: 1 }} size="sm">
          <FormLabel>Search for Challenge</FormLabel>
          <Input
            size="sm"
            placeholder="Enter a keyword or phrase to search..."
            startDecorator={<SearchIcon />}
            onChange={(event) => handleSearchChange(event)} // Use the search input handler here
          />
        </FormControl>
        {renderFilters()}
      </Box>
      {filteredRows.length > 0 ? (
        <Box sx={{ flex: 1, width: "100%" }}>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {filteredRows.map(
              (data: any) =>
                data.live && (
                  <Grid xs={4} key={data.id}>
                    <CustomCard
                      title={data.name}
                      color={typeConfig[data.difficulty]?.color}
                      difficulty={data.difficulty}
                      difficultyColor={typeConfig[data.difficulty]?.color}
                      category={data.category}
                      points={data.points}
                      data={data}
                      onSolve={addSolvedChallenge}
                    />
                  </Grid>
                )
            )}
          </Grid>
        </Box>
      ) : (
        <NoDataComponent />
      )}
    </React.Fragment>
  );
}
